import { useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';
import { waitForTransactionReceipt } from '@wagmi/core';
import { Address } from 'viem';
import { useReadContract, useReadContracts } from 'wagmi';

import { useAuth } from '@contexts/common/AuthorizationContext';

import { config } from '@tools/wagmi';

import { mapWagmiError } from '@helpers/common/error.helpers';

import { useWriteContractExtraGas } from '@utils/contract.utils';

import showToast from '@components/common/Toast';

import { REWARD_POOL } from '@constants/bonds/contracts.constants';
import { CHAIN_ID } from '@constants/common/chain.constants';
import { SuccessMessages } from '@constants/common/messages.constants';
import { ToastType } from '@constants/common/toast.constants';

import abi from '@abi/bonds/rewardPool';

export const usePoolInfo = (chainId: CHAIN_ID) => {
  return useReadContracts({
    contracts: [
      {
        address: REWARD_POOL,
        abi,
        functionName: 'poolInfo',
        chainId,
      },
      {
        address: REWARD_POOL,
        abi,
        functionName: 'rewardToken',
        chainId,
      },
      {
        address: REWARD_POOL,
        abi,
        functionName: 'sharesPerSecond',
        chainId,
      },
    ],
    query: {
      enabled: !!chainId,
      select: (data) => ({
        token: data[0][0],
        lastRewardTime: data[0][1],
        accRewardSharePerShare: data[0][2],
        rewardToken: data[1],
        sharesPerSecond: data[2],
      }),
    },
    allowFailure: false,
  });
};

export const useUserInfo = (chainId: CHAIN_ID) => {
  const { account } = useAuth();

  return useReadContracts({
    contracts: [
      {
        address: REWARD_POOL,
        abi,
        functionName: 'userInfo',
        args: [account as Address],
        chainId,
      },
      {
        address: REWARD_POOL,
        abi,
        functionName: 'pendingShare',
        args: [account as Address],
        chainId,
      },
    ],
    allowFailure: false,
    query: {
      enabled: !!account && !!chainId,
      select: (res) => ({
        amount: res[0][0],
        rewardDebt: res[0][1],
        pendingShare: res[1],
      }),
    },
  });
};

export const useStake = (
  chainId: CHAIN_ID,
  handleUpdate: () => Promise<void>,
) => {
  const queryClient = useQueryClient();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await queryClient.invalidateQueries();

        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Staked,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('Stake:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const stake = useCallback(
    (amountIn: bigint) => {
      writeContract({
        abi,
        address: REWARD_POOL,
        functionName: 'deposit',
        chainId,
        args: [amountIn],
      });
    },
    [writeContract, chainId],
  );

  return {
    stake,
    ...rest,
  };
};

export const useUnstake = (
  chainId: CHAIN_ID,
  handleUpdate: () => Promise<void>,
) => {
  const queryClient = useQueryClient();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await queryClient.invalidateQueries();

        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Unstaked,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('Unstake:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const unstake = useCallback(
    (amountIn: bigint) => {
      writeContract({
        abi,
        address: REWARD_POOL,
        functionName: 'withdraw',
        chainId,
        args: [amountIn],
      });
    },
    [writeContract, chainId],
  );

  return {
    unstake,
    ...rest,
  };
};

export const useClaim = (
  chainId: CHAIN_ID,
  handleUpdate: () => Promise<void>,
) => {
  const queryClient = useQueryClient();

  const { writeContract, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        await queryClient.invalidateQueries();

        await handleUpdate();

        showToast(
          {
            title: SuccessMessages.Claimed,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('Claim:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const claim = useCallback(() => {
    writeContract({
      abi,
      address: REWARD_POOL,
      functionName: 'withdraw',
      chainId,
      args: [BigInt(0)],
    });
  }, [writeContract, chainId]);

  return {
    claim,
    ...rest,
  };
};

type DepositFundsForIntervalParamType = {
  chainId: CHAIN_ID;
  handleUpdate?: () => Promise<void>;
};

export const useDepositFundsForIntervalNft = ({
  chainId,
  handleUpdate,
}: DepositFundsForIntervalParamType) => {
  const { writeContractAsync, ...rest } = useWriteContractExtraGas({
    config,
    mutation: {
      onSuccess: async (data) => {
        const transactionReceipt = await waitForTransactionReceipt(config, {
          hash: data,
        });

        if (handleUpdate) {
          await handleUpdate();
        }

        showToast(
          {
            title: SuccessMessages.Topped,
            withLink: {
              hash: transactionReceipt.transactionHash,
              chainId,
            },
          },
          ToastType.Success,
        );
      },
      onError: (e) => {
        console.log('depositFundsForInterval:', e);
        showToast({ title: mapWagmiError(e) }, ToastType.Error);
      },
    },
  });

  const onWriteContractAsync = useCallback(
    async (amountIn: bigint) => {
      await writeContractAsync({
        abi,
        address: REWARD_POOL,
        functionName: 'depositFundsForInterval',
        chainId,
        args: [amountIn],
      });
    },
    [writeContractAsync, chainId],
  );

  return {
    writeContractAsync: onWriteContractAsync,
    ...rest,
  };
};

export const useGetNextDepositFundsTimeNft = (chainId?: CHAIN_ID) => {
  return useReadContract({
    address: REWARD_POOL,
    abi,
    functionName: 'nextDepositFundsTime',
    chainId,
    query: {
      enabled: !!chainId,
    },
  });
};
